import { useState, useRef, useEffect } from 'react';

type SegmentedControlProps = {
    segments: string[];
    segmentsSpan?: string[];
    segmentsImage?: any;
    selectedSegmentIndex?: number;
    onSegmentChanged?: (segment: number) => void;
    additionalClasses?: string;
    ariaLabel?: string;
    id?: string;
};
export default function SegmentedControlMini({
    segments,
    segmentsSpan = [],
    segmentsImage = [] as any,
    selectedSegmentIndex = 0,
    additionalClasses = '',
    ariaLabel = 'Tab list',
    id = 'segmented-control-mini',
    onSegmentChanged = (index) => {},
}: SegmentedControlProps) {
    const [selectedIndex, setSelectedIndex] = useState(selectedSegmentIndex);
    const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

    // Update selected index when prop changes
    useEffect(() => {
        setSelectedIndex(selectedSegmentIndex);
    }, [selectedSegmentIndex]);

    const handleClick = (index: number) => {
        setSelectedIndex(index);
        onSegmentChanged(index);
    };

    const handleKeyDown = (e: React.KeyboardEvent, index: number) => {
        let newIndex = index;

        switch (e.key) {
            case 'ArrowLeft':
            case 'ArrowUp':
                e.preventDefault();
                newIndex = index === 0 ? segments.length - 1 : index - 1;
                break;
            case 'ArrowRight':
            case 'ArrowDown':
                e.preventDefault();
                newIndex = index === segments.length - 1 ? 0 : index + 1;
                break;
            case 'Home':
                e.preventDefault();
                newIndex = 0;
                break;
            case 'End':
                e.preventDefault();
                newIndex = segments.length - 1;
                break;
            case 'Enter':
            case ' ':
                e.preventDefault();
                handleClick(index);
                return;
            default:
                return;
        }

        // Focus the new tab
        tabRefs.current[newIndex]?.focus();
    };

    return (
        <div
            style={{ maxWidth: '200px', margin: '0px 0px 20px 0px' }}
            className={
                additionalClasses +
                ' ' +
                'segmented-control-mini segmented-control-' +
                segments.length
            }
            role="tablist"
            aria-label={ariaLabel}
        >
            {segments.map((segment, index) => (
                <button
                    ref={(el) => (tabRefs.current[index] = el)}
                    className={
                        index == selectedIndex
                            ? 'segmented-control-segment active'
                            : 'segmented-control-segment'
                    }
                    onClick={handleClick.bind(null, index)}
                    onKeyDown={(e) => handleKeyDown(e, index)}
                    role="tab"
                    aria-selected={index === selectedIndex}
                    aria-controls={`${id}-tabpanel-${index}`}
                    id={`${id}-tab-${index}`}
                    tabIndex={index === selectedIndex ? 0 : -1}
                    key={index}
                    type="button"
                >
                    {segment}
                    {!!segmentsImage[index] && (
                        <div className="segment-image">
                            <img
                                className="inactive"
                                src={`/images/${segmentsImage[index].inactive}`}
                                alt=""
                            />
                            <img
                                className="active"
                                src={`/images/${segmentsImage[index].active}`}
                                alt=""
                            />
                        </div>
                    )}
                    {!!segmentsSpan[index] && (
                        <span style={{ fontSize: 13 }}>{segmentsSpan[index]}</span>
                    )}
                    {index !== selectedIndex && <span className="sr-only">, not selected</span>}
                </button>
            ))}
        </div>
    );
}
